
import axios from 'axios';

const state = () => ({
  summary: null,
  timeSeries: [],
  statusBreakdown: null,
  recent: [],
  attemptsByInvoice: {},
  retryStats: null,
  topErrors: [],
  loading: false,
  error: null,
});

const getters = {
  summary: s => s.summary,
  timeSeries: s => s.timeSeries,
  statusBreakdown: s => s.statusBreakdown,
  recent: s => s.recent,
  attemptsFor: s => inv => s.attemptsByInvoice[inv] || [],
  retryStats: s => s.retryStats,
  topErrors: s => s.topErrors,
  loading: s => s.loading,
  error: s => s.error,
};

const mutations = {
  SET_LOADING(state, v) { state.loading = v; },
  SET_ERROR(state, e) { state.error = e; },
  SET_SUMMARY(state, v) { state.summary = v; },
  SET_TIMESERIES(state, v) { state.timeSeries = v || []; },
  SET_BREAKDOWN(state, v) { state.statusBreakdown = v; },
  SET_RECENT(state, v) { state.recent = v || []; },
  SET_ATTEMPTS_FOR(state, { invoiceNumber, rows }) { state.attemptsByInvoice = { ...state.attemptsByInvoice, [invoiceNumber]: rows || [] }; },
  SET_RETRY_STATS(state, v) { state.retryStats = v; },
  SET_TOP_ERRORS(state, v) { state.topErrors = v || []; },
};

function buildParams(base, extra) {
  const p = new URLSearchParams();
  const a = { ...(base || {}), ...(extra || {}) };
  Object.keys(a).forEach(k => {
    const val = a[k];
    if (val !== undefined && val !== null && val !== '') p.append(k, val);
  });
  return p.toString();
}

/**
 * Returns SAP auth headers for OnlineSalesServer calls, mirroring ViewInvoices flow.
 * Reads apiKey from rootState.integrations.sap.sapApiKey.
 * @param {object} rootState
 * @returns {{[key: string]: string}} headers map (may be empty)
 */
function sapHeaders(rootState) {
  const apiKey = (((rootState || {}).integrations || {}).sap || {}).sapApiKey;
  return apiKey ? { 'X-Api-Key': apiKey } : {};
}

const actions = {
  async fetchSummary({ commit, rootState }, { db, from, to }) {
    commit('SET_LOADING', true); commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db }, { from, to });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/summary?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_SUMMARY', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load summary'); }
    finally { commit('SET_LOADING', false); }
  },

  async fetchTimeSeries({ commit, rootState }, { db, from, to, granularity = 'day' }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db, granularity }, { from, to });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/timeseries?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_TIMESERIES', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load time series'); }
  },

  async fetchStatusBreakdown({ commit, rootState }, { db, from, to }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db }, { from, to });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/status-breakdown?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_BREAKDOWN', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load breakdown'); }
  },

  async fetchRecent({ commit, rootState }, { db, from, to, status, limit = 50 }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db, limit }, { from, to, status });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/recent?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_RECENT', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load recent attempts'); }
  },

  async fetchAttempts({ commit, rootState }, { db, invoiceNumber }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/${encodeURIComponent(invoiceNumber)}/attempts?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_ATTEMPTS_FOR', { invoiceNumber, rows: data });
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load attempts'); }
  },

  async fetchRetryStats({ commit, rootState }, { db, from, to }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db }, { from, to });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/retry-ratio?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_RETRY_STATS', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load retry stats'); }
  },

  async fetchTopErrors({ commit, rootState }, { db, from, to, limit = 10 }) {
    commit('SET_ERROR', null);
    try {
      const qs = buildParams({ db, limit }, { from, to });
      const { data } = await axios.get(`${window.serverApi}dashboard/invoices/top-errors?${qs}`, { headers: sapHeaders(rootState) });
      commit('SET_TOP_ERRORS', data);
    } catch (e) { commit('SET_ERROR', e?.message || 'Failed to load top errors'); }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
